import { store } from "../store";
import { Dark } from "quasar";
import { Loader } from "@googlemaps/js-api-loader";
import { defaults } from "lodash";

// Hard cap on getCurrentPosition. Without an explicit `timeout`, the
// browser implementation has no upper bound — on dev networks where
// Google's wifi-based geolocation API is slow/blocked, or on machines
// where CoreLocation is sluggish, this can hang for minutes and block
// every callsite that awaits it (notably initMap, which leaves the
// page sitting on a loading spinner with no map ever rendered).
const GEOLOCATION_TIMEOUT_MS = 5000;

// A GeolocationPositionError is a number and the browser's own wording
// ("User denied Geolocation"), which formatError cannot put in plain words.
// Callers show the rejection as an error, so it is rejected as the key of
// the message in the `error` catalog that stands for it.
const GEOLOCATION_ERRORS = {
  1: "locationAccessDenied", // PERMISSION_DENIED
  2: "locationUnavailable", // POSITION_UNAVAILABLE
  3: "locationUnavailable", // TIMEOUT
};

export const geolocation = async function () {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Geolocation failed:", error);
          reject(GEOLOCATION_ERRORS[error.code] || "unexpected");
        },
        { timeout: GEOLOCATION_TIMEOUT_MS }
      );
    } else {
      // Browser doesn't support Geolocation
      reject("geolocationUnsupported");
    }
  });
};

const MAP_ID_DARK = "39f7caf189be0dea";
const MAP_ID_LIGHT = "bf43318257e24759";

// Prefer the dedicated Google Maps key (`gmapsApiKey` per .env.template)
// when present so the Maps JavaScript API can have its own key with the
// right referrer restrictions / billing project, and fall back to the
// Firebase Web API key (`apiKey`) for legacy environments where a single
// shared key was configured for both Firebase and Maps in GCP.
//
// Note: process.env.* is substituted at build time by Vite (see
// quasar.config.js extendViteConf). Editing .env.<env> while the dev
// server is running does NOT pick up the new value — restart `yarn dev`
// for env changes to land in the bundle.
const GMAPS_API_KEY = process.env.gmapsApiKey || process.env.apiKey;

if (!GMAPS_API_KEY) {
  // Fail loudly so the page doesn't silently sit blank waiting for a
  // map that will never load. The most common cause is a missing
  // `gmapsApiKey` (or `apiKey`) in the active .env file, or editing
  // the .env file without restarting the dev server.
  console.error(
    "[gmaps] No Google Maps API key configured. " +
      "Set `gmapsApiKey` (preferred) or `apiKey` in .env.<env> and restart `yarn dev`."
  );
}

const loader = new Loader({
  apiKey: GMAPS_API_KEY,
  version: "weekly",
  libraries: ["places"],
});

export const loadGoogleLibrary = async (library) => {
  return loader.importLibrary(library);
};

const mapPool = {
  elements: [],
  async createNewInstance(parent, options) {
    const { Map } = await loadGoogleLibrary("maps");
    const element = document.createElement("DIV");
    element.className = "absolute-full";
    element.style.position = "absolute";
    parent.style.position = "relative";
    parent.appendChild(element);
    const map = new Map(element, options);
    element.map = map;
    map.element = element;
    return map;
  },
  getInstance(parent, options) {
    const element = mapPool.elements.pop();
    if (element) {
      parent.style.position = "relative";
      parent.appendChild(element);
      element.map.setOptions(options);
      return element.map;
    } else {
      return mapPool.createNewInstance(parent, options);
    }
  },
};

// Fallback center for when neither the user record nor the browser
// geolocation prompt give us a starting point — e.g., a fresh user
// with no location set who has previously denied geolocation in this
// browser. Without a fallback, `await geolocation()` rejects and the
// whole initMap chain throws, leaving the page blank with no map ever
// requested. Westerville office (NWD HQ) is a sane default for this
// app's user base and zoomFit() re-centers on the first project batch
// anyway.
const FALLBACK_CENTER = { lat: 40.1013217162639, lng: -82.91483510614476 };

export const initMap = async (element, options = {}) => {
  let center = store.getters.userOrigin;
  if (!center) {
    try {
      center = await geolocation();
    } catch {
      center = FALLBACK_CENTER;
    }
  }
  options = { ...options };
  defaults(options, {
    center,
    zoom: 18,
    clickableIcons: false,
    gestureHandling: "cooperative",
    mapId: Dark.isActive ? MAP_ID_DARK : MAP_ID_LIGHT,
  });
  return mapPool.getInstance(element, options);
};

export const recycleMap = (map) => {
  mapPool.elements.push(map.element);
};

export const geocode = async (request) => {
  const { Geocoder } = await loadGoogleLibrary("geocoding");
  const geocoder = new Geocoder();
  const { results } = await geocoder.geocode(request);
  return results[0];
};
